<script setup lang="ts">
import type { GradeCode } from '~/models/Grade'
import type { ButtonShades } from '~/components/ui/Button.vue'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { faChevronDown } from '@fortawesome/duotone-regular-svg-icons'
import { findGradesByGradeType } from '~/utils/gradeSorter'
import { AuToast } from '~/stores/toasts'
import { useAuthStore } from '~/stores/auth'
import { GradeType } from '~/models/GradeType'
import Button from '~/components/ui/Button.vue'
import AuPopover from '~/components/ui/AuPopover.vue'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'

const { shade = 'light' } = defineProps<{ shade?: ButtonShades }>()

const { t } = useI18n()
const authStore = useAuthStore()
const { isReadOnlyGrade, setGradeType } = authStore
const { gradeCodes } = storeToRefs(authStore)

const model = defineModel<GradeType>()

const grades = computed(() => findGradesByGradeType(GradeType.GradeVgs))

const safeToggleGrade = (grade: GradeCode) => {
  const gradesCopy = [...gradeCodes.value]

  const index = gradesCopy.findIndex(code => code === grade)

  if (index > -1) {
    gradesCopy.splice(index, 1)
  } else {
    gradesCopy.unshift(grade)
  }

  if (gradesCopy.length === 0) {
    AuToast.info(t('settings.vgsWeirdness.cantRemoveLastSelected'))
    return
  }

  if (model.value !== GradeType.GradeVgs) {
    model.value = GradeType.GradeVgs
  }
  setGradeType(GradeType.GradeVgs, gradesCopy)
}
</script>

<template>
  <AuPopover side="bottom" align="end">
    <template #trigger>
      <Button
        shape="square"
        :shade
        class="ml-auto size-[23px] rounded-xs"
        variant="ghost"
      >
        <font-awesome-icon :icon="faChevronDown" />
      </Button>
    </template>

    <template #content>
      <form class="flex flex-col">
        <Button
          v-for="grade in grades"
          :key="grade"
          variant="ghost"
          type="label"
        >
          <AuCheckbox
            :disabled="isReadOnlyGrade(grade)"
            :model-value="gradeCodes.includes(grade)"
            @update:model-value="safeToggleGrade(grade)"
          />
          {{ t(`metadata.grades.${grade}`) }}
        </Button>
      </form>
    </template>
  </AuPopover>
</template>
